import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Chip,
} from '@mui/material';
import { meetingsApi } from '../../../services/api/meetingsApi';

/**
 * MeetingStatusMultiSelector - A multi-select dropdown for filtering by meeting statuses
 * Fetches meeting statuses internally from the API
 *
 * @param {Array} value - Array of selected status objects
 * @param {Function} onChange - Callback when selection changes (array of status objects) => void
 * @param {boolean} disabled - Whether the selector is disabled
 * @param {string} label - Label for the selector
 * @param {string} size - Size of the select ('small' | 'medium')
 * @param {number} minWidth - Minimum width in pixels
 * @param {string} placeholder - Placeholder text when no items selected
 * @param {Object} sx - Additional styles for the FormControl
 */
const MeetingStatusMultiSelector = ({
  value = [],
  onChange,
  disabled = false,
  label = 'Statuses',
  size = 'small',
  minWidth = 180,
  placeholder = 'Statuses...',
  sx = {},
}) => {
  const [statuses, setStatuses] = useState([]);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const data = await meetingsApi.getMeetingStatuses();
        setStatuses(data || []);
      } catch (err) {
        console.error('Failed to fetch meeting statuses:', err);
      }
    };
    fetchStatuses();
  }, []);

  const selectedIds = (value || []).map(v => v.id);

  const handleChange = (event) => {
    const selectedValues = event.target.value;
    // Convert IDs back to full objects
    const selectedObjects = selectedValues
      .map(id => statuses.find(s => s.id === id))
      .filter(Boolean);
    onChange(selectedObjects);
  };

  return (
    <FormControl size={size} sx={{ minWidth, ...sx }} disabled={disabled}>
      {label && <InputLabel>{label}</InputLabel>}
      <Select
        multiple
        value={selectedIds}
        onChange={handleChange}
        label={label}
        displayEmpty
        renderValue={(selected) => {
          if (selected.length === 0) {
            return <em style={{ color: '#9CA3AF' }}>{placeholder}</em>;
          }
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((id) => {
                const status = statuses.find(s => s.id === id);
                if (!status) return null;
                return (
                  <Chip
                    key={id}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: status.color || '#6b7280',
                            flexShrink: 0,
                          }}
                        />
                        {status.name}
                      </Box>
                    }
                    size="small"
                    onDelete={() => {
                      const newValue = (value || []).filter(v => v.id !== id);
                      onChange(newValue);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                );
              })}
            </Box>
          );
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
            },
          },
        }}
      >
        {statuses.map((status) => (
          <MenuItem key={status.id} value={status.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: status.color || '#6b7280',
                  flexShrink: 0,
                }}
              />
              <ListItemText primary={status.name} />
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default MeetingStatusMultiSelector;
